import CustomModal from '@components/CustomModal';
import GridListSubheader from '@components/GridListSubheader';
import AddMoreSeatsModal from '@components/modals/AddMoreSeatsModal';
import CreateEditUserModal from '@components/modals/CreateEditUserModal';
import PaywallModal from '@components/modals/PaywallModal';
import { graphQlClient } from '@config/graphqlClient';
import OrganizationsGraphQL from '@graphql/organization.queries';
import SeatGraphQL from '@graphql/seat.queries';
import { getActiveSubscription } from '@graphql/subscription.queries';
import UserGraphQL from '@graphql/user.queries';
import UserOrganizationGraphQL from '@graphql/userOrganization.queries';
import { useDebounce } from '@hooks/useDebounce';
import { useLoader } from '@hooks/useLoader';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { createOrUpdateOrganizationRequest, getOrganizationsRequest, Organization } from '@models/organization.model';
import { getActiveSubscriptionRequest } from '@models/subscription.model';
import {
  createOrUpdateUserRequest,
  disableOrEnableUserRequest,
  getPaginatedUsersRequest,
  User,
  UserRoles,
  UserStatus
} from '@models/user.model';
import { Box } from '@mui/material';
import UserList from '@pages/users/list';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { RootState } from '@redux/store';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

interface UsersPageProps {}

const UsersPage: React.FC<UsersPageProps> = () => {
  // common
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { setLoading } = useLoader();
  const localeCommon = useTranslation('common');
  const localeUsers = useTranslation('users');
  const localeActionMessages = useTranslation('actionMessages');
  const { role } = useAppSelector((state: RootState) => state.user);
  const { name: organizationName, organizationId, onboardingDetails } = useAppSelector((state: RootState) => state.organization);

  // Redirect non-allowed to processes page
  useEffect(() => {
    if (!role || ![UserRoles.SUPER_ADMIN, UserRoles.OWNER, UserRoles.ADMIN].includes(role as UserRoles)) {
      history.push('/processes');
    }
  }, [role, history]);

  // list
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[] | null>(null);
  const [count, setCount] = useState<number>(0);
  // search
  const [search, setSearch] = useState<string>('');
  const debouncedSearchValue = useDebounce(search, 500);
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };
  // filter
  const [filter, setFilter] = useState<string>('Active');
  const handleFilterChange = (filter: string) => {
    setFilter(filter);
  };
  // sort
  const [orderBy, setOrderBy] = useState<string | null>('name');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // pagination
  const [page, setPage] = useState<number>(0);
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(event.target.value as unknown as number);
    setPage(0);
  };

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [isFirstUser, setIsFirstUser] = useState<boolean>(false);
  const [showPaywallPopup, setShowPaywallPopup] = useState<boolean>(false);
  const [showAddSeatsPopup, setShowAddSeatsPopup] = useState<boolean>(false);
  const [seatTypeNeeded, setSeatTypeNeeded] = useState<'admin' | 'user' | undefined>(undefined);
  const [availableRoles, setAvailableRoles] = useState<UserRoles[]>([]);
  const [isAdminRestricted, setIsAdminRestricted] = useState<boolean>(false);
  const [availableAdminSeats, setAvailableAdminSeats] = useState<number>(0);
  const [availableUserSeats, setAvailableUserSeats] = useState<number>(0);

  // initialize
  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page, debouncedSearchValue, filter, orderBy, orderDirection]);

  useEffect(() => {
    if (role === UserRoles.SUPER_ADMIN) {
      getOrganizations();
    } else {
      setOrganizations([{ organizationId: organizationId || '', name: organizationName || '' }]);
    }
  }, []);

  const getUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data: getPaginatedUsersRequest = await graphQlClient.request(
        role === UserRoles.SUPER_ADMIN
          ? UserGraphQL.queries.getPaginatedUsers
          : UserGraphQL.queries.getPaginatedUsersWithRole,
        {
          users: {
            page,
            limit: rowsPerPage,
            search: debouncedSearchValue,
            filter,
            orderBy,
            orderDirection,
          },
        },
      );
      setCount(data?.getPaginatedUsers?.count || 0);
      setUsers(data?.getPaginatedUsers?.users || []);
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filter, localeCommon, orderBy, orderDirection, page, rowsPerPage, debouncedSearchValue]);

  const getOrganizations = useCallback(async () => {
    try {
      const data: getOrganizationsRequest = await graphQlClient.request(OrganizationsGraphQL.queries.getOrganizations, {
        organizations: {
          page,
          // TODO: Check limit
          limit: 1000,
          search: debouncedSearchValue,
          filter,
          orderBy,
          orderDirection,
        },
      });
      setOrganizations(data?.getOrganizations?.organizations || []);
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modalClose = (): void => {
    setShowCreateModal(false);
  };

  const handleAddUser = async () => {
    // Get fresh user count to determine if this is the first user
    let freshUserCount = 0;
    try {
      const data: getPaginatedUsersRequest = await graphQlClient.request(
        role === UserRoles.SUPER_ADMIN
          ? UserGraphQL.queries.getPaginatedUsers
          : UserGraphQL.queries.getPaginatedUsersWithRole,
        {
          users: {
            page: 0,
            limit: 1000,
            search: '',
            filter: 'ACTIVE',
            orderBy: null,
            orderDirection: 'asc',
          },
        },
      );
      freshUserCount = data?.getPaginatedUsers?.count || 0;
    } catch (error) {
      console.error('Error fetching user count:', error);
    }

    // Check if this is the first user (count <= 1, considering owner might exist)
    // For the first user, skip subscription checks and force USER role
    if (freshUserCount <= 1) {
      setIsFirstUser(true);
      setIsAdminRestricted(false);
      setShowCreateModal(true);
      return;
    }

    // For subsequent users, perform subscription checks
    setIsFirstUser(false);
    let data: getActiveSubscriptionRequest | null = null;
    try {
      data = await graphQlClient.request(getActiveSubscription);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }

    // No subscription - show admin as restricted and allow only USER role
    if (!data || !data?.getActiveSubscription) {
      setIsAdminRestricted(true);
      setAvailableRoles([UserRoles.USER]);
      setAvailableAdminSeats(0);
      setAvailableUserSeats(0);
      setShowCreateModal(true);
      return;
    }

    const subscription = data.getActiveSubscription;
    const adminSeatsAvailable = (subscription.adminSeats || 0) - (subscription.adminSeatsInUse || 0);
    const userSeatsAvailable = (subscription.userSeats || 0) - (subscription.userSeatsInUse || 0);
    setAvailableAdminSeats(adminSeatsAvailable);
    setAvailableUserSeats(userSeatsAvailable);

    // Has subscription but no seats available - show add seats modal
    if (adminSeatsAvailable === 0 && userSeatsAvailable === 0) {
      dispatch(
        appendActionMessage({
          message: `${localeUsers['noSeatsAvailableMessage']} ${localeUsers['goToBilling']}.`,
          type: SnackType.WARNING,
        }),
      );
      setShowAddSeatsPopup(true);
      return;
    }

    // Determine which roles are available based on seat availability
    const rolesAvailable: UserRoles[] = [];
    if (adminSeatsAvailable > 0) {
      rolesAvailable.push(UserRoles.ADMIN);
    }
    if (userSeatsAvailable > 0) {
      rolesAvailable.push(UserRoles.USER);
    }

    // Set admin as restricted if no admin seats available
    const adminRestricted = adminSeatsAvailable === 0;
    setIsAdminRestricted(adminRestricted);

    // Check if only one type of seat is available - show warning but still open dialog
    if (adminSeatsAvailable > 0 && userSeatsAvailable === 0) {
      dispatch(
        appendActionMessage({
          message: `${localeUsers['onlyAdminSeatsAvailable']}.`,
          type: SnackType.WARNING,
        }),
      );
    }

    if (userSeatsAvailable > 0 && adminSeatsAvailable === 0) {
      dispatch(
        appendActionMessage({
          message: `${localeUsers['onlyUserSeatsAvailable']}.`,
          type: SnackType.WARNING,
        }),
      );
    }

    setAvailableRoles(rolesAvailable);
    // Open the modal (either with both seat types or with one type available)
    setShowCreateModal(true);
  };

  const toggleEnableDisableUser = async (userId: string, enable: boolean = false) => {
    if (!userId) return;
    try {
      const query = enable ? UserGraphQL.mutations.enableUser : UserGraphQL.mutations.disableUser;
      // For non-SUPER_ADMIN, pass organizationId to enable/disable for this organization only
      const variables: { id: string; organizationId?: string } = { id: userId };
      if (role !== UserRoles.SUPER_ADMIN && organizationId) {
        variables.organizationId = organizationId;
      }
      const result: disableOrEnableUserRequest = await graphQlClient.request(query, variables);
      if (result?.disableUser?.userId || result?.enableUser?.userId) {
        dispatch(
          appendActionMessage({
            message: enable
              ? localeActionMessages['userEnabledSuccessfully']
              : localeActionMessages['userDisabledSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      getUsers();
    }
  };

  const resetFailedLoginCount = async (userId?: string) => {
    if (!userId) return;
    setSaving(true);
    try {
      const query = UserGraphQL.mutations.updateOtherUser;
      const params: any = {
        user: {
          userId,
          failedLoginAttempts: 0,
        },
      };
      const data: createOrUpdateUserRequest = await graphQlClient.request(query, params);
      if (data?.updateOtherUser?.userId) {
        dispatch(
          appendActionMessage({
            message: localeActionMessages['failedLoginAttemptsReset'],
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setSaving(false);
      getUsers();
    }
  };

  const updateOrganizationUserCreated = async () => {
    try {
      const data: createOrUpdateOrganizationRequest = await graphQlClient.request(
        OrganizationsGraphQL.mutations.updateOrganization,
        {
          organization: {
            organizationId: organizationId || '',
            onboardingDetails: {
              ...onboardingDetails,
              userCreated: true,
            },
          },
        },
      );
      if (data?.updateOrganization?.organizationId) {
        dispatch(setOrganizationData(data.updateOrganization));
      }
    } catch (error) {
      console.error('Failed to update user created status:', error);
    }
  };

  const createUser = async (
    _organizationId: string,
    email: string,
    firstName: string,
    lastName: string,
    userId?: string,
    role?: UserRoles,
    seatAssignmentId?: string,
    currentRole?: UserRoles,
  ) => {
    if (!email) return;

    // If is has a userId, it's an edit
    const isEdit = !!userId;
    setSaving(true);
    try {
      if (isEdit) {
        // Update user details (firstName, lastName, email)
        const userUpdateParams: any = {
          user: {
            userId,
            firstName,
            lastName,
            email,
          },
        };

        await graphQlClient.request(UserGraphQL.mutations.updateOtherUser, userUpdateParams);

        // Update role if provided
        if (role !== undefined && userId) {
          // If role is changing and user has a seat, revoke it first
          if (role !== currentRole && seatAssignmentId) {
            try {
              await graphQlClient.request(SeatGraphQL.mutations.revokeSeat, {
                input: {
                  seatAssignmentId,
                },
              });
            } catch (seatError) {
              console.warn('Failed to revoke seat:', seatError);
              // Continue with role update even if seat revocation fails
            }
          }

          // Get userOrganizationId by querying the user's organizations
          const userOrgsData: any = await graphQlClient.request(
            UserOrganizationGraphQL.queries.getUserOrganizations,
            { userId }
          );

          // Find the userOrganization record for the current organization
          const userOrg = userOrgsData.getUserOrganizations?.find(
            (uo: any) => uo.organizationId === organizationId
          );

          if (userOrg?.userOrganizationId) {
            const roleUpdateParams = {
              input: {
                userOrganizationId: userOrg.userOrganizationId,
                role,
              },
            };
            await graphQlClient.request(
              UserOrganizationGraphQL.mutations.updateUserOrganizationRole,
              roleUpdateParams
            );
          }
        }

        dispatch(
          appendActionMessage({
            message: localeActionMessages['userEditedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
      } else {
        // Creating new user
        const params: any = {
          user: {
            firstName,
            lastName,
            email,
            organizationId: organizationId,
            status: UserStatus.ACTIVE,
          },
        };
        const data: createOrUpdateUserRequest = await graphQlClient.request(UserGraphQL.mutations.createUser, params);

        if (data?.createUser?.userId) {
          if (!onboardingDetails?.userCreated) {
            updateOrganizationUserCreated();
          }
          dispatch(
            appendActionMessage({
              message: localeActionMessages['userCreatedSuccessfully'],
              type: SnackType.SUCCESS,
            }),
          );
        }
      }
    } catch (e: any) {
      console.log('e', e);
      const errorMessage = e?.response?.errors[0]?.message || localeCommon['requestError'];

      // Check if the error is about seat availability and determine the type
      const isAdminSeatError = errorMessage.toLowerCase().includes('no admin seat available');
      const isUserSeatError = errorMessage.toLowerCase().includes('no user seat available');
      const isSeatError = isAdminSeatError || isUserSeatError;

      // Show error message to user
      dispatch(
        appendActionMessage({
          message: errorMessage,
          type: SnackType.ERROR,
        }),
      );

      if (isSeatError) {
        // Determine which seat type is needed
        const neededSeatType = isAdminSeatError ? 'admin' : 'user';
        setSeatTypeNeeded(neededSeatType);

        // Close the create modal and open the add seats modal
        setShowCreateModal(false);
        setShowAddSeatsPopup(true);
      }

      setSaving(false);
      getUsers();

      // Don't close modal if we're showing the add seats popup
      if (!isSeatError) {
        setShowCreateModal(false);
      }

      // Exit early to avoid the finally block closing the modal
      return;
    } finally {
      setSaving(false);
      getUsers();
      setShowCreateModal(false);
    }
  };

  const createUserByEmail = async (
    organizationId: string,
    email: string,
    role: UserRoles,
  ) => {
    if (!email) return;

    setSaving(true);
    try {
      const data: createOrUpdateUserRequest = await graphQlClient.request(UserGraphQL.mutations.createUserByEmail, {
        user: {
          email,
          role,
          organizationId,
        },
      });
      if (data?.createUserByEmail?.userId) {
        if (!onboardingDetails?.userCreated) {
          updateOrganizationUserCreated();
        }
        dispatch(
          appendActionMessage({
            message: localeActionMessages['userCreatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (e: any) {
      console.log('e', e);
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setSaving(false);
      getUsers();
      setShowCreateModal(false);
    }
  };

  const handleUpgradeModal = () => {
    setShowPaywallPopup(false);
    history.push('/billing');
  };

  const handleAdminRestrictedClick = () => {
    setShowCreateModal(false);
    setShowPaywallPopup(true);
  };

  return (
    <Box className={`w-full mb-28`}>
      <Box className="sticky top-0 z-30 bg-backgroundUI">
        <GridListSubheader
          title={localeUsers['title']}
          buttonText={localeUsers['addUser']}
          isGridView={false}
          onToggleView={() => {}}
          onDrawerOpen={handleAddUser}
          icon="user"
          showFavGrid={false}
        />
      </Box>{' '}
      <UserList
        handleDisableUser={toggleEnableDisableUser}
        handleEnableUser={(id) => toggleEnableDisableUser(id, true)}
        handleEditUser={createUser}
        count={count}
        users={users || []}
        organizations={organizations || []}
        page={page}
        handleChangePage={handleChangePage}
        rowsPerPage={rowsPerPage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        search={search}
        handleSearchChange={handleSearchChange}
        orderDirection={orderDirection}
        orderBy={orderBy}
        handleRequestSort={handleRequestSort}
        filter={filter}
        handleFilterChange={handleFilterChange}
        resetFailedLoginCount={resetFailedLoginCount}
      />
      <CustomModal isOpen={!!showCreateModal} onClose={modalClose} title={localeUsers['addUser']} maxWidth="600px">
        <CreateEditUserModal
          modalClose={modalClose}
          createUser={createUser}
          createUserByEmail={createUserByEmail}
          organizations={organizations || []}
          saving={saving}
          useSimplifiedCreation={true}
          isFirstUser={isFirstUser}
          availableRoles={availableRoles}
          isAdminRestricted={isAdminRestricted}
          onAdminRestrictedClick={handleAdminRestrictedClick}
          availableAdminSeats={availableAdminSeats}
          availableUserSeats={availableUserSeats}
        />
      </CustomModal>
      <CustomModal
        isOpen={!!showPaywallPopup}
        maxWidth="800px"
        rounded
        onClose={() => setShowPaywallPopup(false)}
        onConfirm={() => setShowPaywallPopup(false)}>
        <PaywallModal handleUpgrade={handleUpgradeModal} />
      </CustomModal>
      <CustomModal
        isOpen={!!showAddSeatsPopup}
        maxWidth="800px"
        rounded
        onClose={() => {
          setShowAddSeatsPopup(false);
          setSeatTypeNeeded(undefined);
        }}
        onConfirm={() => {
          setShowAddSeatsPopup(false);
          setSeatTypeNeeded(undefined);
        }}>
        <AddMoreSeatsModal handleUpgrade={handleUpgradeModal} seatType={seatTypeNeeded} />
      </CustomModal>
    </Box>
  );
};

export default UsersPage;
